import { CheckCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import type { AlertProps, StepProps } from 'antd';
import { Button, Form, Modal, message } from 'antd';
import classNames from 'classnames';
import { useContext, useEffect, useMemo, useState } from 'react';

import { AlertModal } from 'app/components/commons/AlertModal/AlertModal';
import { FormLegacyRenderProp } from 'app/components/forms/FormLegacyRenderProp';
import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { LegacyHotelContext } from 'app/context/HotelContext/LegacyHotelContext';
import { useDisconnect } from 'app/hooks/data/autoInventory/useDisconnect';
import { useSave } from 'app/hooks/data/autoInventory/useSave';
import { CORE_QUERY_KEYS as HOTEL_INFO_QUERY_KEYS } from 'app/hooks/data/core/extranet/hotel';
import { getInventoryPath } from 'app/hooks/data/hotels/extranet/useInventory';
import { hotelPath } from 'app/hooks/data/hotels/useHotel';
import { extranetPackagePath } from 'app/hooks/data/packages/usePackageExtranet';
import type {
  RoomScrapped,
  RoomsWithInventory,
} from 'app/typings/autoInventory';
import { ModifierType, SyncStatus } from 'app/typings/autoInventory';
import type { ExtranetRoom } from 'app/typings/packages';
import { amplitudeTrack } from 'app/utils/track/amplitude';

import './AutoInventoryModal.scss';
import type { FormValues } from './Utils';
import { checkIfBarIsValid, checkIfRoomMappingIsValid } from './Utils';
import { AutoInventoryConfiguration } from './_components/AutoInventoryConfiguration/AutoInventoryConfiguration';
import { AutoInventoryExplanation } from './_components/AutoInventoryExplanation/AutoInventoryExplanation';
import { Footer } from './_components/Footer';

type AutoInventoryModalProps = {
  hasAutoInventoryActivated: boolean;
  isAutoInventoryModalOpen: boolean;
  onClose: () => void;
};

export const AutoInventoryModal = ({
  hasAutoInventoryActivated,
  isAutoInventoryModalOpen,
  onClose,
}: AutoInventoryModalProps) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const { hotelId, hotelInfo } = useContext(HotelContext);
  const { publishedPackages, roomsFromPublishedPackages: rooms } =
    useContext(LegacyHotelContext);
  const { mutate: disconnectAutoInventory, isPending: isDisconnectLoading } =
    useDisconnect(hotelId);

  const [initConfiguration, setInitConfiguration] = useState(
    !hotelInfo?.stockScrapperEnabled
  );

  const { mutate: saveBookingConfiguration } = useSave(hotelId);

  const [currentStep, setCurrentStep] = useState(0);
  const [discountStepHasError, setDiscountStepHasError] = useState(false);
  const [isModalClosedBlock, setIsModalClosedBlock] = useState(false);

  const [alertModalOpen, setAlertModalOpen] = useState(false);
  const [isAlertForDisconnect, setIsAlertForDisconnect] = useState(false);

  const [scrappingRooms, setScrappingRooms] = useState<RoomScrapped[]>([]);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>(SyncStatus.Idle);

  const [scrappingInventory, setScrappingInventory] = useState<
    RoomsWithInventory[]
  >([]);

  const initialValue: FormValues = useMemo(
    () => ({
      rooms:
        rooms?.reduce(
          (acc, room, idx) => {
            if (room.bookingcomId && room.bookingcomName) {
              acc[idx] = {
                ...room,
                AutoInventoryId: room.bookingcomId,
                AutoInventoryName: room.bookingcomName,
              };
            }

            return acc;
          },
          {} as {
            [index: number]: ExtranetRoom & {
              AutoInventoryId?: number;
              AutoInventoryName?: string;
            };
          }
        ) ?? {},
      matchOTAPrice: !hasAutoInventoryActivated
        ? undefined
        : !hotelInfo?.stockScrapperBarModifierValue,
      customBar: {
        value:
          (hotelInfo?.stockScrapperBarModifierType === ModifierType.RATE
            ? Math.round((hotelInfo?.stockScrapperBarModifierValue ?? 0) * 100)
            : (hotelInfo?.stockScrapperBarModifierValue ?? 0)) ?? undefined,
        suffix: hotelInfo?.stockScrapperBarModifierType ?? ModifierType.RATE,
      },
      packageRules:
        publishedPackages.map((pkg) => ({
          packageId: pkg.id,
          packageName: pkg.aliasForHotel,
          discount: Math.round((pkg?.autoDiscountRate ?? 0) * 100) || undefined,
          lastMinuteDays: pkg?.lastMinuteAutoDiscountDays ?? undefined,
          lastMinuteDiscountValue:
            Math.round((pkg?.lastMinuteAutoDiscountRate ?? 0) * 100) ||
            undefined,
          lastMinuteIsActivated: !!(
            pkg?.lastMinuteAutoDiscountRate && pkg?.lastMinuteAutoDiscountDays
          ),
          discountIsActivated: !!initConfiguration || !!pkg?.autoDiscountRate,
        })) || [],
      overrideOpenings: false,
    }),
    [
      hasAutoInventoryActivated,
      publishedPackages,
      initConfiguration,
      hotelInfo?.stockScrapperBarModifierValue,
      hotelInfo?.stockScrapperBarModifierType,
      rooms,
    ]
  );

  const [formData, setFormData] = useState<FormValues>(initialValue);

  useEffect(() => {
    form.setFieldsValue(initialValue);
  }, [form, initialValue]);

  const resetStore = () => {
    queryClient.invalidateQueries({
      queryKey: [hotelPath, { hotelId: hotelInfo?.id }],
    });
    queryClient.invalidateQueries({
      queryKey: [HOTEL_INFO_QUERY_KEYS.hotelInfo, { id: hotelInfo?.id }],
    });

    queryClient.invalidateQueries({ queryKey: [getInventoryPath] });
    queryClient.invalidateQueries({
      queryKey: ['/hotels/:id/rooms', { id: hotelId }],
    });
    queryClient.invalidateQueries({
      queryKey: [extranetPackagePath, { hotelId }],
    });
  };

  const onDisconnect = () => {
    amplitudeTrack('Click disconnect');
    disconnectAutoInventory(undefined, {
      onSuccess: () => {
        resetStore();
        onClose();
        setAlertModalOpen(false);
      },
      onError: () => {
        message.error('Error while disconnecting Auto inventory');
      },
    });
  };

  const onFinish = async () => {
    const formattedValues = {
      roomMappings: Object.values(formData.rooms).map((room) => ({
        id: room.id,
        scrappedRoomId: room.AutoInventoryId,
        scrappedRoomName: room.AutoInventoryName,
      })),
      barModifier: !formData.matchOTAPrice
        ? {
            value:
              formData.customBar.suffix === ModifierType.RATE
                ? (formData.customBar.value ?? 0) / 100
                : (formData.customBar.value ?? 0),
            modifierType:
              formData.customBar.suffix === ModifierType.RATE
                ? ModifierType.RATE
                : ModifierType.FLAT,
          }
        : undefined,
      packageRules: formData.packageRules.map((rule) => ({
        packageId: rule.packageId,
        discount: rule.discount ? rule.discount / 100 : undefined,
        lastMinuteDiscount: rule.lastMinuteIsActivated
          ? {
              discount: (rule.lastMinuteDiscountValue ?? 0) / 100,
              days: rule.lastMinuteDays ?? 0,
            }
          : undefined,
      })),
      overrideOpenings: formData.overrideOpenings,
    };

    saveBookingConfiguration(formattedValues, {
      onSuccess: () => {
        resetStore();
        onClose();
      },
      onError: () => {
        message.error('Error while saving the data');
      },
    });
  };

  const handleClose = async () => {
    setCurrentStep(0);
    amplitudeTrack('Close Auto inventory modal');
    onClose();
  };

  const alertModalInfo: AlertProps = {
    type: isAlertForDisconnect ? 'warning' : 'success',
    icon: isAlertForDisconnect ? (
      <InfoCircleOutlined />
    ) : (
      <CheckCircleOutlined />
    ),
    message: isAlertForDisconnect
      ? 'Deactivate Auto inventory?'
      : 'Demand successfully sent!',
    className: isAlertForDisconnect ? 'alert-disconnect' : '',
    description: isAlertForDisconnect ? (
      <>
        <div className="alertBody">
          By deactivating Auto inventory, only your manual data will remain.
        </div>
        <div className="alert-footer">
          <Button
            loading={isDisconnectLoading}
            type="default"
            className="disconnect-button"
            onClick={onDisconnect}
            disabled={isDisconnectLoading}
          >
            Deactivate
          </Button>
          <Button
            disabled={isDisconnectLoading}
            type="primary"
            onClick={() => {
              amplitudeTrack('Click Stay connected');
              setAlertModalOpen(false);
            }}
          >
            Keep activated
          </Button>
        </div>
      </>
    ) : (
      'Your Account Manager has been contacted.'
    ),
    closable: !isAlertForDisconnect,
    onClose: () => {
      amplitudeTrack('Close notification');
      setAlertModalOpen(false);
      handleClose();
    },
    style: isAlertForDisconnect ? { backgroundColor: '#FFF', border: 0 } : {},
  };

  return (
    <FormLegacyRenderProp
      form={form}
      onFinish={onFinish}
      initialValues={initialValue}
    >
      {(values) => {
        const items: StepProps[] = [
          {
            title: 'Room mapping',
            disabled: false,
          },
          {
            title: 'BAR',
            disabled: !checkIfRoomMappingIsValid(values),
          },
          {
            title: 'Discount',
            disabled: !checkIfBarIsValid(values),
          },
          {
            title: 'Preview',
            disabled: true,
          },
        ];

        return (
          <>
            <Modal
              centered
              destroyOnClose={true}
              width={initConfiguration ? 496 : 684}
              maskClosable={!isModalClosedBlock}
              open={isAutoInventoryModalOpen}
              className={classNames([
                'auto-inventory-modal',
                {
                  'explication-part': initConfiguration,
                },
              ])}
              title={initConfiguration ? <></> : 'Auto inventory'}
              footer={
                initConfiguration ? (
                  <></>
                ) : (
                  <Footer
                    setCurrentStep={setCurrentStep}
                    setAlertModalOpen={setAlertModalOpen}
                    setScrappingInventory={setScrappingInventory}
                    setIsAlertForDisconnect={setIsAlertForDisconnect}
                    setFormData={setFormData}
                    currentStep={currentStep}
                    items={items}
                    hasAutoInventoryActivated={hasAutoInventoryActivated}
                    values={values}
                    hotelId={hotelId}
                    discountStepHasError={discountStepHasError}
                    setIsModalClosedBlock={setIsModalClosedBlock}
                  />
                )
              }
              onCancel={handleClose}
            >
              {initConfiguration ? (
                <AutoInventoryExplanation
                  setInitConfiguration={setInitConfiguration}
                  onClose={onClose}
                  hotelId={hotelId}
                  setScrappingRooms={setScrappingRooms}
                  setSyncStatus={setSyncStatus}
                  syncStatus={syncStatus}
                  setAlertModalOpen={setAlertModalOpen}
                  setIsAlertForDisconnect={setIsAlertForDisconnect}
                />
              ) : (
                <AutoInventoryConfiguration
                  currentStep={currentStep}
                  setCurrentStep={setCurrentStep}
                  items={items}
                  hotelId={hotelId}
                  values={values}
                  setFormData={setFormData}
                  scrappingRooms={scrappingRooms}
                  scrappingInventory={scrappingInventory}
                  setDiscountStepHasError={setDiscountStepHasError}
                  setScrappingRooms={setScrappingRooms}
                  setSyncStatus={setSyncStatus}
                  setInitConfiguration={setInitConfiguration}
                  packages={publishedPackages}
                />
              )}
            </Modal>
            {alertModalOpen && (
              <AlertModal {...alertModalInfo} isOpen={alertModalOpen} />
            )}
          </>
        );
      }}
    </FormLegacyRenderProp>
  );
};
